/**
 * Users — User management list page
 *
 * Navigates to /admin/users/:id for full-page detail editor.
 */

import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ListPageTemplate from "templates/ListPageTemplate";
import KDBox from "components/KDBox";
import KDButton from "components/KDButton";
import useUsers from "hooks/useUsers";
import { useT } from "context/I18nContext";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";
import InviteModal from "./components/InviteModal";

function Users() {
  const t = useT();
  const ability = useAbility();
  const navigate = useNavigate();
  const { users, loading, error } = useUsers({ autoFetch: true });
  const [inviteOpen, setInviteOpen] = useState(false);

  const canEdit = ability.can(ACTIONS.UPDATE, SUBJECTS.USER);
  const canInvite = ability.can(ACTIONS.INVITE, SUBJECTS.USER);

  const columns = useMemo(
    () => [
      {
        field: "displayName",
        header: t("users.displayName"),
        width: "20%",
        render: (val) => val || "—",
      },
      {
        field: "email",
        header: t("common.email"),
        width: "25%",
      },
      {
        field: "activeRole",
        header: t("common.role"),
        width: "12%",
        render: (val) => (
          <Chip
            label={val || "—"}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          />
        ),
      },
      {
        field: "suspended",
        header: t("common.status"),
        width: "12%",
        render: (val) => (
          <Chip
            label={val ? t("users.suspended") : t("common.active")}
            size="small"
            color={val ? "error" : "success"}
          />
        ),
      },
      {
        field: "createdAt",
        header: t("users.createdAt"),
        width: "16%",
        render: (val) => {
          if (!val) return "—";
          const d = val._seconds ? new Date(val._seconds * 1000) : new Date(val);
          return d.toLocaleDateString();
        },
      },
      {
        field: "actions",
        header: t("common.actions"),
        width: "15%",
        align: "right",
        sortable: false,
        render: (_, row) => (
          <KDBox display="flex" justifyContent="flex-end" gap={0.5}>
            {canEdit && (
              <Tooltip title={t("common.edit")}>
                <IconButton
                  size="small"
                  color="info"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/users/${row.uid}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </KDBox>
        ),
      },
    ],
    [t, canEdit, navigate]
  );

  const handleRowClick = useCallback(
    (row) => navigate(`/admin/users/${row.uid}`),
    [navigate]
  );

  return (
    <>
      <ListPageTemplate
        title={t("nav.users")}
        columns={columns}
        rows={users}
        loading={loading}
        error={error}
        emptyMessage={t("users.empty")}
        onRowClick={handleRowClick}
        searchable
        searchPlaceholder={t("users.search")}
        defaultSortField="displayName"
        headerColor="primary"
        defaultVisibleFields={["displayName", "email", "activeRole", "suspended", "createdAt", "actions"]}
        extraToolbarActions={
          canInvite && (
            <KDButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setInviteOpen(true)}
              sx={{
                borderRadius: "10px",
                background: "linear-gradient(135deg, #0D9488 0%, #5EEAD4 100%)",
                "&:hover": { background: "linear-gradient(135deg, #0B8276 0%, #4DD9C7 100%)" },
              }}
            >
              <PersonAddIcon sx={{ fontSize: 18, mr: 0.5 }} />
              {t("users.invite")}
            </KDButton>
          )
        }
      />
      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
    </>
  );
}

export default Users;
